/**
 * Gift Bundle Selection — Custom Element for Shopify (Hyper Theme)
 *
 * Builds on a parallel feature set to Hyper's products-bundle-selection:
 *  - Multi-product bundle with item-count-based discount tiers (Mengenrabatt)
 *  - PLUS value-based gift tiers: when bundle subtotal crosses a threshold,
 *    a gift variant is automatically added to /cart/add.js as a separate
 *    line-item with property `_gift_for_bundle = true`.
 *
 * The gift mechanism requires a 0,00 € variant of each gift product to exist
 * in the shop (no Discount-App needed). See INSTALLATION.md.
 */

(function () {
  'use strict';

  if (customElements.get('gift-bundle-selection')) return;

  // -----------------------------
  // Money formatting (Shopify-compatible)
  // -----------------------------
  function formatMoney(cents, format) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    const placeholderRe = /\{\{\s*(\w+)\s*\}\}/;
    if (!format) format = '${{amount}}';

    function thousands(value, sep) {
      return value.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    }
    function withDecimals(value, decimals, decSep, thousSep) {
      const n = (value / 100).toFixed(decimals);
      const parts = n.split('.');
      return thousands(parts[0], thousSep) + (decimals ? decSep + parts[1] : '');
    }

    const match = format.match(placeholderRe);
    if (!match) return format;

    let amount = '';
    switch (match[1]) {
      case 'amount':
        amount = withDecimals(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals':
        amount = withDecimals(cents, 0, '.', ',');
        break;
      case 'amount_with_comma_separator':
        amount = withDecimals(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = withDecimals(cents, 0, ',', '.');
        break;
      case 'amount_with_apostrophe_separator':
        amount = withDecimals(cents, 2, '.', "'");
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = withDecimals(cents, 0, ',', ' ');
        break;
      case 'amount_with_space_separator':
        amount = withDecimals(cents, 2, ',', ' ');
        break;
      default:
        amount = withDecimals(cents, 2, '.', ',');
    }
    return format.replace(placeholderRe, amount);
  }

  function safeJsonParse(value, fallback) {
    if (!value) return fallback;
    try {
      return JSON.parse(value);
    } catch (e) {
      console.warn('[gift-bundle-selection] JSON parse error', e, value);
      return fallback;
    }
  }

  function dispatchCartRefresh() {
    // Try common Hyper / theme conventions
    const events = [
      'cart:item-added',
      'cart:refresh',
      'cart-drawer:open',
      'on:cart:add',
    ];
    events.forEach((name) => {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
    });
    // Try opening a cart drawer if one exists
    const drawer = document.querySelector(
      'cart-drawer, [data-cart-drawer], [is="cart-drawer"]'
    );
    if (drawer) {
      if (typeof drawer.open === 'function') drawer.open();
      else if (typeof drawer.show === 'function') drawer.show();
      else drawer.setAttribute('open', 'true');
    }
  }

  // -----------------------------
  // Custom Element
  // -----------------------------
  class GiftBundleSelection extends HTMLElement {
    connectedCallback() {
      // Avoid double-init
      if (this._initialized) return;
      this._initialized = true;

      // ---- Configuration from data-attributes ----
      this.sectionId = this.dataset.sectionId;
      this.maxItems = parseInt(this.dataset.maxItems, 10) || 6;
      this.preventDuplicates = this.dataset.preventDuplicateItems === 'true';
      this.moneyFormat = this.dataset.moneyFormat || '€{{amount}}';
      this.discountTiers = (safeJsonParse(this.dataset.discountTiers, []) || [])
        .filter((t) => t && t.percent > 0 && t.min_items > 0)
        .sort((a, b) => a.min_items - b.min_items);
      this.giftTiers = (safeJsonParse(this.dataset.giftTiers, []) || [])
        .filter((t) => t && t.threshold_cents > 0 && t.name)
        .sort((a, b) => a.threshold_cents - b.threshold_cents);

      // Limit picker to ONE selection at a time:
      this.maxGiftSelection = 1;

      this.i18n = {
        addToCart: this.dataset.i18nAddToCart || 'In den Warenkorb',
        remove: this.dataset.i18nRemove || 'Entfernen',
      };

      // ---- State ----
      this.selectedItems = []; // array of { uid, productId, variantId, productTitle, variantTitle, price, image }
      this.uidCounter = 0;
      this.selectedGiftTier = null; // tier number or null — only ONE gift can be selected at a time

      // ---- DOM refs ----
      this.cards = Array.from(this.querySelectorAll('.gbs-card'));
      this.slotsContainer = this.querySelector('[data-slots-container]');
      this.slotsWrap = this.querySelector('[data-slots-wrap]');
      this.slotsEmpty = this.querySelector('[data-slots-empty]');
      this.totalEl = this.querySelector('[data-total]');
      this.discountRow = this.querySelector('[data-discount-row]');
      this.discountAmount = this.querySelector('[data-discount-amount]');
      this.countBadge = this.querySelector('.gbs-bar__count');
      this.addButton = this.querySelector('[data-add-button]');
      this.errorEl = this.querySelector('[data-error]');
      this.helpText = this.querySelector('[data-help-text]');
      this.giftRows = Array.from(this.querySelectorAll('[data-gift-tier][data-threshold-cents]'));
      this.giftHint = this.querySelector('[data-gift-hint]');
      const wrapper = this.closest('.section__wrapper');
      this.giftPills = wrapper
        ? Array.from(wrapper.querySelectorAll('.gift-tier-pill'))
        : [];
      // Tabs (live in section header)
      this.tabButtons = wrapper
        ? Array.from(wrapper.querySelectorAll('.gbs-tab'))
        : [];
      this.tabPanels = Array.from(this.querySelectorAll('.gbs-tabs__panel'));

      // ---- Wire events ----
      this.cards.forEach((card) => this._wireCard(card));
      this.tabButtons.forEach((btn) => this._wireTab(btn));
      this.giftRows.forEach((row) => this._wireGiftRow(row));
      if (this.addButton) {
        this.addButton.addEventListener('click', (e) => this._handleAddToCart(e));
      }

      // First render
      this._render();
    }

    // -----------------------------
    // Wiring
    // -----------------------------
    _wireCard(card) {
      const select = card.querySelector('[data-variant-select]');
      const priceEl = card.querySelector('[data-card-price]');
      const imageEl = card.querySelector('[data-card-image]');
      const addBtn = card.querySelector('[data-add-to-bundle]');

      if (select && priceEl) {
        select.addEventListener('change', () => {
          const opt = select.options[select.selectedIndex];
          const price = parseInt(opt.dataset.price, 10) || 0;
          priceEl.textContent = formatMoney(price, this.moneyFormat);
          const newImg = opt.dataset.image;
          if (newImg && imageEl) {
            imageEl.src = newImg;
            imageEl.removeAttribute('srcset');
          }
        });
      }

      if (addBtn) {
        addBtn.addEventListener('click', () => this._addCardToBundle(card));
      }
    }

    _wireTab(btn) {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const idx = btn.dataset.tabIndex;
        this.tabButtons.forEach((b) =>
          b.classList.toggle('active', b.dataset.tabIndex === idx)
        );
        this.tabPanels.forEach((p) =>
          p.classList.toggle('hidden', p.dataset.tabIndex !== idx)
        );
      });
    }

    _wireGiftRow(row) {
      const handler = (e) => {
        if (e.target.closest('a')) return; // let product link clicks through
        if (e.target.closest('button')) return; // don't catch nested buttons
        const tierIdx = parseInt(row.dataset.giftTier, 10);
        const tier = this.giftTiers.find((t) => t.tier === tierIdx);
        if (!tier) return;
        const sub = this._subtotalCents();
        if (sub < tier.threshold_cents) return; // locked
        this.selectedGiftTier = tierIdx;
        this._render();
      };
      row.addEventListener('click', handler);
      row.addEventListener('keydown', (e) => {
        if (e.target.closest('a')) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handler(e);
        }
      });
    }

    // -----------------------------
    // Selection logic
    // -----------------------------
    _addCardToBundle(card) {
      if (this.selectedItems.length >= this.maxItems) {
        this._showError(`Maximum erreicht: ${this.maxItems} Produkte.`);
        return;
      }
      const variants = safeJsonParse(card.dataset.variants, []) || [];
      const select = card.querySelector('[data-variant-select]');
      let chosen;
      if (select) {
        const id = parseInt(select.value, 10);
        chosen = variants.find((v) => v.id === id);
      } else {
        chosen = variants.find((v) => v.available) || variants[0];
      }
      if (!chosen) {
        this._showError('Diese Variante ist nicht verfügbar.');
        return;
      }
      if (!chosen.available) {
        this._showError('Diese Variante ist ausverkauft.');
        return;
      }

      if (this.preventDuplicates) {
        const dup = this.selectedItems.find((i) => i.variantId === chosen.id);
        if (dup) {
          this._showError('Dieses Produkt ist bereits im Bundle.');
          return;
        }
      }

      this._clearError();
      this.selectedItems.push({
        uid: 'i' + ++this.uidCounter,
        productId: card.dataset.productId,
        productTitle: card.dataset.productTitle,
        variantId: chosen.id,
        variantTitle: chosen.title,
        price: chosen.price,
        image: chosen.image || this._cardImage(card),
      });
      this._render();
      this._flashAddedFeedback(card);
    }

    _flashAddedFeedback(card) {
      // Card pulse + Button "✓ Hinzugefügt" for 1.2s — works on all viewports
      card.classList.add('is-just-added');
      setTimeout(() => card.classList.remove('is-just-added'), 600);

      const addBtn = card.querySelector('[data-add-to-bundle]');
      if (addBtn) {
        const textEl = addBtn.querySelector('.btn__text');
        if (textEl && !addBtn.dataset.originalText) {
          addBtn.dataset.originalText = textEl.textContent;
        }
        addBtn.classList.add('is-just-added');
        if (textEl) textEl.textContent = '✓ Hinzugefügt';
        clearTimeout(addBtn._resetTimer);
        addBtn._resetTimer = setTimeout(() => {
          addBtn.classList.remove('is-just-added');
          if (textEl) textEl.textContent = addBtn.dataset.originalText || '+ Hinzufügen';
        }, 1200);
      }

      // Toast on mobile/tablet — Desktop sees the sidebar, no toast needed
      const isMobile = window.matchMedia('(max-width: 1023.98px)').matches;
      if (isMobile) this._showMobileToast();
    }

    _ensureToast() {
      if (this.toast) return this.toast;
      this.toast = document.createElement('div');
      this.toast.className = 'gbs-toast';
      this.toast.setAttribute('role', 'status');
      this.toast.setAttribute('aria-live', 'polite');
      this.toast.innerHTML =
        '<span class="gbs-toast__icon" aria-hidden="true">' +
        '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
        '<polyline points="2.5 7 6 10.5 11.5 4"/></svg>' +
        '</span>' +
        '<div class="gbs-toast__text">' +
        '<div class="gbs-toast__title"></div>' +
        '<div class="gbs-toast__sub"></div>' +
        '</div>';
      this.toastTitle = this.toast.querySelector('.gbs-toast__title');
      this.toastSub = this.toast.querySelector('.gbs-toast__sub');
      document.body.appendChild(this.toast);
      return this.toast;
    }

    _showMobileToast() {
      this._ensureToast();
      const count = this.selectedItems.length;
      const total = formatMoney(this._subtotalCents(), this.moneyFormat);
      this.toastTitle.textContent = 'Zum Bundle hinzugefügt';
      this.toastSub.textContent =
        count + ' ' + (count === 1 ? 'Produkt' : 'Produkte') + ' · ' + total;

      // Force restart of CSS transition by toggling classes via rAF
      this.toast.classList.remove('is-visible');
      // eslint-disable-next-line no-unused-expressions
      this.toast.offsetHeight;
      this.toast.classList.add('is-visible');

      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        this.toast.classList.remove('is-visible');
      }, 2200);
    }

    _cardImage(card) {
      const img = card.querySelector('[data-card-image]');
      return img ? img.currentSrc || img.src : '';
    }

    _removeItem(uid) {
      this.selectedItems = this.selectedItems.filter((i) => i.uid !== uid);
      this._clearError();
      this._render();
    }

    // -----------------------------
    // Calculations
    // -----------------------------
    _subtotalCents() {
      return this.selectedItems.reduce((sum, it) => sum + (it.price || 0), 0);
    }

    _activeDiscount() {
      const count = this.selectedItems.length;
      let active = null;
      for (const tier of this.discountTiers) {
        if (count >= tier.min_items) active = tier;
      }
      return active;
    }

    _availableGiftTiers() {
      const sub = this._subtotalCents();
      return this.giftTiers.filter((t) => sub >= t.threshold_cents);
    }

    _selectedGift() {
      if (this.selectedGiftTier == null) return null;
      const available = this._availableGiftTiers();
      return available.find((t) => t.tier === this.selectedGiftTier) || null;
    }

    // -----------------------------
    // Render
    // -----------------------------
    _render() {
      this._renderSlots();
      this._renderTotal();
      this._renderGiftProgress();
      this._renderAddButton();
      this._renderCount();
    }

    _renderSlots() {
      if (!this.slotsContainer) return;
      // Clear container — we rebuild on every render so the slot count
      // matches the actual selected items (no fixed-height empty placeholders).
      this.slotsContainer.innerHTML = '';
      this.selectedItems.forEach((item) => {
        const slot = this._createSlotElement(item);
        this.slotsContainer.appendChild(slot);
      });
      // Toggle wrap visibility / empty hint
      const hasItems = this.selectedItems.length > 0;
      if (this.slotsWrap) this.slotsWrap.hidden = !hasItems;
      if (this.slotsEmpty) this.slotsEmpty.hidden = hasItems;
    }

    _createSlotElement(item) {
      const slot = document.createElement('div');
      slot.className = 'gbs-bar__slot';
      slot.dataset.uid = item.uid;

      const content = document.createElement('div');
      content.className = 'gbs-bar__slot-content';

      if (item.image) {
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = item.productTitle;
        img.loading = 'lazy';
        content.appendChild(img);
      } else {
        const span = document.createElement('span');
        span.className = 'gbs-bar__slot-text';
        span.textContent = (item.productTitle || '').slice(0, 14);
        content.appendChild(span);
      }

      const removeBtn = document.createElement('button');
      removeBtn.className = 'gbs-bar__slot-remove btn btn--white';
      removeBtn.type = 'button';
      removeBtn.setAttribute('aria-label', 'Entfernen');
      removeBtn.innerHTML =
        '<span class="btn__text flex items-center">' +
        '<svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">' +
        '<line x1="1" y1="1" x2="13" y2="13"/>' +
        '<line x1="13" y1="1" x2="1" y2="13"/>' +
        '</svg></span>';
      removeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._removeItem(item.uid);
      });

      slot.appendChild(content);
      slot.appendChild(removeBtn);
      return slot;
    }

    _renderTotal() {
      const sub = this._subtotalCents();
      const discount = this._activeDiscount();
      let displayTotal = sub;
      if (discount) {
        displayTotal = Math.round(sub * (1 - discount.percent / 100));
        if (this.discountRow) this.discountRow.hidden = false;
        if (this.discountAmount)
          this.discountAmount.textContent = `−${formatMoney(
            sub - displayTotal,
            this.moneyFormat
          )} (${discount.percent}%)`;
      } else {
        if (this.discountRow) this.discountRow.hidden = true;
      }
      if (this.totalEl)
        this.totalEl.textContent = formatMoney(displayTotal, this.moneyFormat);
    }

    _renderGiftProgress() {
      // Use BRUTTO subtotal (before discount) as threshold base — matches how
      // Leon described it ("ab 50 € Warenwert").
      const sub = this._subtotalCents();
      const available = this._availableGiftTiers();

      // Sync selectedGiftTier with current availability:
      // - if currently selected is no longer available → drop to null
      // - never auto-select; user must actively pick
      const currentlyValid =
        this.selectedGiftTier != null &&
        available.some((t) => t.tier === this.selectedGiftTier);
      if (!currentlyValid) {
        this.selectedGiftTier = null;
      }

      // Show hint when at least one gift is available but none selected
      if (this.giftHint) {
        this.giftHint.hidden = !(
          available.length > 0 && this.selectedGiftTier === null
        );
      }

      this.giftRows.forEach((row) => {
        const tierIndex = parseInt(row.dataset.giftTier, 10);
        const tier = this.giftTiers.find((t) => t.tier === tierIndex);
        if (!tier) return;
        const threshold = tier.threshold_cents;
        const unlocked = sub >= threshold;
        const selected = unlocked && this.selectedGiftTier === tierIndex;

        const fillEl = row.querySelector('[data-progress-fill]');
        const txtEl = row.querySelector('[data-progress-text]');
        const badge = row.querySelector('[data-gift-badge]');

        const pct = Math.min(100, (sub / threshold) * 100);
        if (fillEl) fillEl.style.width = pct.toFixed(1) + '%';
        row.classList.toggle('is-unlocked', unlocked);
        row.classList.toggle('is-selected', selected);
        row.classList.toggle('is-available', unlocked && !selected);

        // a11y: only unlocked rows are clickable
        if (unlocked) {
          row.setAttribute('role', 'button');
          row.setAttribute('tabindex', '0');
          row.setAttribute(
            'aria-pressed',
            selected ? 'true' : 'false'
          );
        } else {
          row.removeAttribute('role');
          row.removeAttribute('tabindex');
          row.removeAttribute('aria-pressed');
        }

        if (txtEl) {
          if (selected) {
            txtEl.textContent = `Dein Geschenk: ${tier.name}`;
          } else if (unlocked) {
            txtEl.textContent = 'Tippen zum Auswählen';
          } else {
            const missing = threshold - sub;
            txtEl.textContent =
              'Noch ' +
              formatMoney(missing, this.moneyFormat) +
              ' bis zum ' +
              tier.name;
          }
        }
        if (badge) {
          const lockedEl = badge.querySelector('.badge-locked');
          const availEl = badge.querySelector('.badge-available');
          const selEl = badge.querySelector('.badge-selected');
          if (lockedEl) lockedEl.hidden = unlocked;
          if (availEl) availEl.hidden = !unlocked || selected;
          if (selEl) selEl.hidden = !selected;
        }
      });

      // Highlight pills in section header
      this.giftPills.forEach((pill) => {
        const idx = parseInt(pill.dataset.giftTier, 10);
        const tier = this.giftTiers.find((t) => t.tier === idx);
        if (!tier) return;
        pill.classList.toggle('is-unlocked', sub >= tier.threshold_cents);
      });
    }

    _renderAddButton() {
      if (!this.addButton) return;
      const count = this.selectedItems.length;
      this.addButton.disabled = count === 0;
    }

    _renderCount() {
      if (this.countBadge)
        this.countBadge.textContent = this.selectedItems.length;
    }

    // -----------------------------
    // Add to cart
    // -----------------------------
    async _handleAddToCart(e) {
      e.preventDefault();
      if (this.selectedItems.length === 0) return;
      this._setLoading(true);
      this._clearError();

      const items = this.selectedItems.map((it) => ({
        id: it.variantId,
        quantity: 1,
        properties: {},
      }));

      // Apply discount tier label as a property so it shows on the order.
      const discount = this._activeDiscount();
      if (discount) {
        items.forEach((i) => {
          i.properties._bundle_discount = `${discount.percent}%`;
        });
      }

      // Nur EIN Geschenk wird hinzugefügt — das aktuell ausgewählte.
      // Property ohne Underscore-Prefix → sichtbar im Cart-Drawer und Order.
      const selected = this._selectedGift();
      if (selected && items.length > 0) {
        items[0].properties['Geschenk'] = `${selected.name} (gratis)`;
      }

      try {
        const res = await fetch(
          `${window.routes?.cart_add_url || '/cart/add'}.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
            },
            body: JSON.stringify({ items }),
          }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Fehler beim Hinzufügen.');
        }
        await res.json();

        // Reset state
        this.selectedItems = [];
        this._render();

        dispatchCartRefresh();
      } catch (err) {
        console.error('[gift-bundle-selection] add to cart failed', err);
        this._showError(err.message || 'Konnte Bundle nicht hinzufügen.');
      } finally {
        this._setLoading(false);
      }
    }

    _setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      if (this.addButton) {
        this.addButton.classList.toggle('is-loading', isLoading);
        this.addButton.disabled = isLoading || this.selectedItems.length === 0;
      }
    }

    _showError(msg) {
      if (!this.errorEl) {
        console.warn('[gift-bundle-selection]', msg);
        return;
      }
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }

    _clearError() {
      if (this.errorEl) {
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }
    }
  }

  function escapeHtml(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  customElements.define('gift-bundle-selection', GiftBundleSelection);
})();

/* ====================================================================
   Mobile-Layout: Bundle-Bar nach oben verschieben
   --------------------------------------------------------------------
   Auf Mobile (<=1023px) wird die .gbs-bar-column an die Pills-Position
   in den .section__header geschoben (über die Tabs / Produktliste).
   Auf Desktop bleibt sie an ihrer Original-Position (Sidebar rechts).

   Sicher gegenüber dem Web-Component: Das Component fasst seine
   DOM-Referenzen einmalig im connectedCallback (this.querySelector...).
   Diese Referenzen + Event-Listener hängen an den Knoten, nicht an der
   Position — ein Verschieben der Bar bricht also nichts. Dieser Code
   läuft auf DOMContentLoaded, d.h. garantiert NACH dem Component-Init.
   ==================================================================== */
(function () {
  'use strict';

  var MOBILE = '(max-width: 1023.98px)';

  function setup(gbs) {
    if (gbs._mobileRelocateInit) return;
    gbs._mobileRelocateInit = true;

    var wrapper = gbs.closest('.section__wrapper');
    var bar = gbs.querySelector('.gbs-bar-column');
    if (!bar) return;

    var header = wrapper ? wrapper.querySelector('.section__header') : null;
    var headerText = header
      ? header.querySelector('.section__header--text')
      : null;

    // Platzhalter merkt sich die Original-Position (Desktop-Restore).
    var placeholder = document.createComment('gbs-bar-anchor');
    bar.parentNode.insertBefore(placeholder, bar);

    function apply(isMobile) {
      if (isMobile) {
        if (!header) return;
        // Schon im Header? Nichts tun.
        if (bar.parentNode === header) return;
        // An Pills-Position einfügen: direkt nach dem Header-Textblock
        // (dort sitzen auch die Pills), sonst ans Header-Ende.
        if (headerText && headerText.parentNode === header) {
          header.insertBefore(bar, headerText.nextSibling);
        } else {
          header.appendChild(bar);
        }
        bar.classList.add('gbs-bar-column--mobile-top');
      } else {
        // Zurück an die Original-Position (vor dem Platzhalter).
        if (placeholder.parentNode && bar.parentNode !== placeholder.parentNode) {
          placeholder.parentNode.insertBefore(bar, placeholder);
        }
        bar.classList.remove('gbs-bar-column--mobile-top');
      }
    }

    var mql = window.matchMedia(MOBILE);
    apply(mql.matches);

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', function (e) { apply(e.matches); });
    } else if (typeof mql.addListener === 'function') {
      // Safari < 14 Fallback
      mql.addListener(function (e) { apply(e.matches); });
    }
  }

  function init() {
    var nodes = document.querySelectorAll('gift-bundle-selection');
    for (var i = 0; i < nodes.length; i++) setup(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();